import { Input, Select, SelectOption } from 'commons/components';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import type { PocInputValidity } from 'commons/types';
import { MATERIAL_DEFINITION_TYPES } from 'commons-app/types/material-definition.type';
import { toStringValue } from 'commons/utils/string.utils';
import { useTranslation } from 'core/i18n/i18n';
import { useMemo } from 'react';
import { DeviceTypeSummary } from 'modules/settings/pods/settings-materials/material-definition.vm';
import {
    LONG_NAME_MAX_LENGTH,
    NAME_MAX_LENGTH,
    PROVIDER_MAX_LENGTH,
} from 'modules/settings/pods/settings-materials/material-definitions.constants';
import { MaterialDefinitionFormState } from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.model';
import {
    MaterialDefinitionFormCard,
    MaterialDefinitionFormCardContent,
} from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.style';
import { FormErrors } from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.validation';

interface GeneralInfoCardProps {
    formState: MaterialDefinitionFormState;
    onFieldChange: (
        field: keyof MaterialDefinitionFormState,
        value: MaterialDefinitionFormState[keyof MaterialDefinitionFormState]
    ) => void;
    onValidity?: (field: string, validity: PocInputValidity) => void;
    errors: FormErrors;
    deviceTypes: DeviceTypeSummary[];
}

export const GeneralInfoCard: React.FC<GeneralInfoCardProps> = ({
    formState,
    onFieldChange,
    onValidity,
    errors,
    deviceTypes,
}) => {
    const { t } = useTranslation();

    const typeOptions = useMemo<SelectOption[]>(
        () =>
            MATERIAL_DEFINITION_TYPES.map((type) => ({
                value: type,
                text: t(`settings.materials.material-definitions.type.${type}`),
            })),
        [t]
    );

    const deviceTypeOptions = useMemo<SelectOption[]>(
        () => deviceTypes.map((dt) => ({ value: dt.id, text: dt.name })),
        [deviceTypes]
    );

    const handleChange = (field: string, value: string | (string | number)[] | null | undefined) => {
        onFieldChange(field as keyof MaterialDefinitionFormState, toStringValue(value));
    };

    return (
        <MaterialDefinitionFormCard>
            <PocTypography variant="subtitle1">
                {t('settings.materials.material-definitions.form.material-details.section-title')}
            </PocTypography>
            <MaterialDefinitionFormCardContent>
                <Input
                    name="name"
                    label="settings.materials.material-definitions.form.fields.name"
                    placeholder="settings.materials.material-definitions.form.fields.name-placeholder"
                    value={formState.name}
                    required
                    autofocus
                    spaceForError
                    maxlength={NAME_MAX_LENGTH}
                    validity={errors.name ? { state: 'error', type: ['custom'] } : undefined}
                    validationMessages={errors.name ? { error: { custom: t(errors.name) } } : undefined}
                    onChange={(field, value) => handleChange(field, value ?? '')}
                    onValidity={onValidity}
                />

                <Input
                    name="longName"
                    label="settings.materials.material-definitions.form.fields.long-name"
                    placeholder="settings.materials.material-definitions.form.fields.long-name-placeholder"
                    value={formState.longName}
                    spaceForError
                    maxlength={LONG_NAME_MAX_LENGTH}
                    validity={errors.longName ? { state: 'error', type: ['custom'] } : undefined}
                    validationMessages={errors.longName ? { error: { custom: t(errors.longName) } } : undefined}
                    onChange={(field, value) => handleChange(field, value ?? '')}
                    onValidity={onValidity}
                />

                <Input
                    name="provider"
                    label="settings.materials.material-definitions.form.fields.provider"
                    placeholder="settings.materials.material-definitions.form.fields.provider-placeholder"
                    value={formState.provider}
                    required
                    spaceForError
                    maxlength={PROVIDER_MAX_LENGTH}
                    validity={errors.provider ? { state: 'error', type: ['custom'] } : undefined}
                    validationMessages={errors.provider ? { error: { custom: t(errors.provider) } } : undefined}
                    onChange={(field, value) => handleChange(field, value ?? '')}
                    onValidity={onValidity}
                />

                <Select
                    name="type"
                    label="settings.materials.material-definitions.form.fields.type"
                    placeholder="settings.materials.material-definitions.form.fields.type-placeholder"
                    options={typeOptions}
                    value={formState.type || undefined}
                    required
                    spaceForError
                    validity={errors.type ? { state: 'error', type: ['custom'] } : undefined}
                    validationMessages={errors.type ? { error: { custom: t(errors.type) } } : undefined}
                    onChange={handleChange}
                    onValidity={onValidity}
                />

                <Select
                    name="deviceTypeId"
                    label="settings.materials.material-definitions.form.fields.device-type"
                    placeholder="settings.materials.material-definitions.form.fields.device-type-placeholder"
                    options={deviceTypeOptions}
                    value={formState.deviceTypeId || undefined}
                    required
                    searchable
                    spaceForError
                    validity={errors.deviceTypeId ? { state: 'error', type: ['custom'] } : undefined}
                    validationMessages={errors.deviceTypeId ? { error: { custom: t(errors.deviceTypeId) } } : undefined}
                    onChange={handleChange}
                    onValidity={onValidity}
                />
            </MaterialDefinitionFormCardContent>
        </MaterialDefinitionFormCard>
    );
};
